import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from '../../core/services/api-client.service';
import {
  AskResult,
  BatchWithExtractions,
  CaseExtraction,
  ConfirmAction,
  DocumentBatch,
  ExtractedCaseFields,
} from './document-ocr.models';

const ALLOWED_MIME: Record<string, true> = {
  'application/pdf': true,
  'image/jpeg': true,
  'image/jpg': true,
  'image/png': true,
  'image/webp': true,
};

/** Must match backend/src/routes/intake.js's MAX_BYTES — checked here too so an oversized file
 *  fails immediately instead of wasting time reading + base64-encoding it first. */
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

@Injectable({ providedIn: 'root' })
export class DocumentOcrService {
  private readonly api = inject(ApiClientService);

  private readonly items = signal<BatchWithExtractions[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  readonly batches = () =>
    [...this.items()].sort(
      (a, b) => new Date(b.batch.createdAt).getTime() - new Date(a.batch.createdAt).getTime(),
    );

  getBatchWithExtractions(batchId: number): BatchWithExtractions | undefined {
    return this.items().find((x) => x.batch.id === batchId);
  }

  getExtraction(extractionId: number): CaseExtraction | undefined {
    for (const item of this.items()) {
      const found = item.extractions.find((e) => e.id === extractionId);
      if (found) return found;
    }
    return undefined;
  }

  async refresh(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const res = await firstValueFrom(this.api.get<{ items: BatchWithExtractions[] }>('/documents/intake'));
      this.items.set(res.items || []);
    } catch (err) {
      this.error.set(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      this.loading.set(false);
    }
  }

  async fetchExtraction(extractionId: number): Promise<CaseExtraction> {
    const extraction = await firstValueFrom(
      this.api.get<CaseExtraction>(`/documents/intake/extractions/${extractionId}`),
    );
    this.applyExtractionUpdate(extraction);
    return extraction;
  }

  async refreshOne(batchId: number): Promise<BatchWithExtractions | undefined> {
    const res = await firstValueFrom(this.api.get<BatchWithExtractions>(`/documents/intake/${batchId}`));
    this.items.update((list) => {
      const rest = list.filter((x) => x.batch.id !== res.batch.id);
      return [res, ...rest];
    });
    return res;
  }

  /** Reads the file as base64 and uploads it — throws if the type isn't supported. */
  async upload(file: File): Promise<DocumentBatch> {
    if (!ALLOWED_MIME[file.type]) {
      throw new Error(`Unsupported file type “${file.type || 'unknown'}”. Use PDF, JPEG, PNG, or WEBP.`);
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      throw new Error(`File too large (${(file.size / (1024 * 1024)).toFixed(1)} MB) — max 5 MB.`);
    }
    const contentBase64 = await this.readAsBase64(file);
    const batch = await firstValueFrom(
      this.api.post<DocumentBatch>('/documents/intake', { mimeType: file.type, contentBase64 }),
    );
    await this.refresh();
    return batch;
  }

  async correctExtraction(extractionId: number, patch: Partial<ExtractedCaseFields>): Promise<CaseExtraction> {
    const updated = await firstValueFrom(
      this.api.patch<CaseExtraction>(`/documents/intake/extractions/${extractionId}`, { extractedJson: patch }),
    );
    this.applyExtractionUpdate(updated);
    return updated;
  }

  async confirmExtraction(extractionId: number, action: ConfirmAction, grievanceRef?: number): Promise<CaseExtraction> {
    const updated = await firstValueFrom(
      this.api.post<CaseExtraction>(`/documents/intake/extractions/${extractionId}/confirm`, {
        action,
        grievanceRef,
      }),
    );
    this.applyExtractionUpdate(updated);
    return updated;
  }

  /** Downloads the original uploaded scan for a batch (same pattern as ops.service.ts's case attachments). */
  async downloadSourceFile(batchId: number, fileName = `case-${batchId}`): Promise<void> {
    const blob = await firstValueFrom(this.api.getBlob(`/documents/intake/${batchId}/file`));
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Fetches the original scan for inline preview (image or PDF, whichever it is).
   * Caller owns the returned object URL and must revoke it when done (e.g. ngOnDestroy).
   */
  async getSourcePreview(batchId: number): Promise<{ url: string; mimeType: string }> {
    const blob = await firstValueFrom(this.api.getBlob(`/documents/intake/${batchId}/file`));
    return { url: URL.createObjectURL(blob), mimeType: blob.type };
  }

  /**
   * Same as getSourcePreview, but cropped to just this one extraction's own pages when the
   * upload had several back-to-back applications — a reviewer should only see the case they're
   * actually reviewing, not the other cases that happened to share the same scan.
   */
  async getExtractionSourcePreview(extractionId: number): Promise<{ url: string; mimeType: string }> {
    const blob = await firstValueFrom(this.api.getBlob(`/documents/intake/extractions/${extractionId}/file`));
    return { url: URL.createObjectURL(blob), mimeType: blob.type };
  }

  /** Downloads just this extraction's own pages — see getExtractionSourcePreview. */
  async downloadExtractionSourceFile(extractionId: number, fileName = `case-${extractionId}`): Promise<void> {
    const blob = await firstValueFrom(this.api.getBlob(`/documents/intake/extractions/${extractionId}/file`));
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  ask(
    question: string,
    grievanceId?: number | string,
    sessionId?: string,
  ): Promise<AskResult> {
    return firstValueFrom(
      this.api.post<AskResult>('/cases/ask', { question, grievanceId, sessionId }),
    );
  }

  /** Restore logged conversation_json for the Ask AI chat UI. */
  getAskConversation(sessionId: string): Promise<{
    sessionId: string;
    conversationJson: Array<{ que: string; ans: string }>;
    contextJson?: unknown;
  } | null> {
    const sid = String(sessionId || '').trim();
    if (!sid) return Promise.resolve(null);
    return firstValueFrom(
      this.api.get<{
        sessionId: string;
        conversationJson: Array<{ que: string; ans: string }>;
        contextJson?: unknown;
      }>(`/cases/ask/sessions/${encodeURIComponent(sid)}`),
    ).catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : '';
      if (/not found/i.test(msg) || /404/.test(msg)) return null;
      throw err;
    });
  }

  private applyExtractionUpdate(updated: CaseExtraction): void {
    this.items.update((list) =>
      list.map((item) =>
        item.batch.id === updated.batchRef
          ? { ...item, extractions: item.extractions.map((e) => (e.id === updated.id ? updated : e)) }
          : item,
      ),
    );
  }

  private readAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Could not read the file'));
      reader.onload = () => {
        const result = String(reader.result || '');
        const base64 = result.includes(',') ? result.split(',').pop()! : result;
        resolve(base64);
      };
      reader.readAsDataURL(file);
    });
  }
}
